import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { ActionLink } from "@/components/shared/action-buttons"
import { useIndexProps } from "./page-props"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  educatorsTeacherTeamMember,
  newEducatorsTeacherTeamMember,
} from "@/routes"
import {
  ExternalLink,
  Inbox,
  UserMinus,
  UserPlus,
  UserSearch,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Link, usePathParam } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation(["team_members", "common"])
  const { members, team_name } = useIndexProps()
  const teacherId = usePathParam("teacherId")
  const teamId = usePathParam("teamId")

  return (
    <>
      <EducatorPageChrome
        records={{ "educators/teams/edit": team_name }}
        action={
          <ActionLink
            variant="solid"
            href={newEducatorsTeacherTeamMember({ teacherId, teamId }).url}
            Icon={UserPlus}
          >
            {t("index.create")}
          </ActionLink>
        }
      />
      <PageBody role="region">
        <Card data-testid="member-list">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{team_name}</CardTitle>
            {members.length > 0 && (
              <Link
                href="/search/educators?listing_type.keyword=personal"
                as={Button}
              >
                <UserSearch />
                {t("index.find")}
              </Link>
            )}
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-6 py-6">
            {members.length == 0 ? (
              <Empty className="col-span-3">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("index.empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("index.empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <div className="flex flex-row gap-6">
                    <Link
                      href="/search/educators?listing_type.keyword=team"
                      as={Button}
                    >
                      <UserSearch />
                      {t("index.find")}
                    </Link>
                    <Link
                      href={
                        newEducatorsTeacherTeamMember({ teacherId, teamId }).url
                      }
                      as={Button}
                    >
                      <UserPlus />
                      {t("index.create")}
                    </Link>
                  </div>
                </EmptyContent>
              </Empty>
            ) : (
              members.map((member) => (
                <Card className="w-full" data-testid="member-card">
                  <CardHeader className="flex flex-row items-center justify-between align-bottom">
                    <CardTitle data-testid="member-name">
                      {member.teacher_name}
                    </CardTitle>
                  </CardHeader>
                  <CardFooter className="flex flex-row flex-wrap gap-4">
                    <a
                      href={`/search/educators/${member.personal_profile_document_id}`}
                      target="_blank"
                      className={buttonVariants()}
                    >
                      <ExternalLink />
                      {t("index.profile")}
                    </a>
                    <ConfirmationDialog
                      action={
                        <Link
                          className={buttonVariants({ variant: "destructive" })}
                          href={
                            educatorsTeacherTeamMember({
                              teacherId,
                              teamId,
                              id: member.id,
                            }).url
                          }
                          method="delete"
                          as={Button}
                        >
                          {t("common:actions.confirm")}
                        </Link>
                      }
                    >
                      <ConfirmationDialogTrigger asChild>
                        <Button variant={"destructive-outline"}>
                          <UserMinus />
                          {t("common:actions.remove")}
                        </Button>
                      </ConfirmationDialogTrigger>
                    </ConfirmationDialog>
                  </CardFooter>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
